import { Address, Details, DetailsInput, ItemInput, ItemInputCategories } from '../../gql/graphql';

type AddressInput = { line1: string; line2?: string | null; city: string; state: string; zip: string };

const toAddressInput = (address?: Address | null): AddressInput | undefined =>
  address?.line1 ? { line1: address.line1, line2: address.line2 ?? undefined, city: address.city, state: address.state, zip: address.zip } : undefined;

const toItemInput = (item: Details['items'][number]): ItemInput => ({
  category: (item.category as unknown as ItemInputCategories) ?? undefined,
  displayName: item.displayName,
  isLeaseable: item.isLeaseable,
  itemImageUrl: item.itemImageUrl,
  itemUrl: item.itemUrl,
  price: item.price,
  quantity: item.quantity,
  sku: item.sku,
});

/**
 * Builds the createOrder details input from the order the session started on, so a replacement
 * order minted mid-session keeps the Shop First cart (items, shipping, tax) and the customer
 * details the integration already collected. Returns undefined when there is nothing to carry over.
 *
 * cellPhone is the number the customer just entered; it is required on CustomerInput and is the
 * reason the replacement order exists in the first place (OR-3290).
 */
export const getNewOrderDetailsFromOrderDetails = (orderDetails: Details | undefined, cellPhone: string): DetailsInput | undefined => {
  const items = orderDetails?.items?.filter((item) => item.quantity > 0 && !!item.displayName).map(toItemInput) ?? [];
  const customer = orderDetails?.customer;
  const billingAddress = toAddressInput(customer?.billingAddress);
  const shippingAddress = toAddressInput(customer?.shippingAddress);
  const hasCustomerDetails = !!(customer?.firstName || customer?.lastName || customer?.emailAddress || billingAddress || shippingAddress);

  if (!items.length && !hasCustomerDetails) {
    return undefined;
  }

  const details: DetailsInput = {};

  if (items.length) {
    details.items = items;
    // AES rejects items without a shipping amount, so default to zero when the original order has none
    details.shippingAmnt = orderDetails?.shippingAmnt || '0.00';
    if (orderDetails?.taxAmnt) {
      details.taxAmnt = orderDetails.taxAmnt;
    }
  }

  if (hasCustomerDetails) {
    details.customer = {
      cellPhone,
      firstName: customer?.firstName || undefined,
      lastName: customer?.lastName || undefined,
      emailAddress: customer?.emailAddress || undefined,
      billingAddress,
      shippingAddress,
    };
  }

  return details;
};
